import { useApolloClient } from '@apollo/client/react';
import {
  Flexbox,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { AssetLimitForNetAvailability } from '@see-power-pft-common-js/net-availability/types';
import { UK_TIMEZONE } from '@see-power-pft-common-js/utils/product-mapper';
import { useAssetAvailabilities } from 'pages/Availability/AssetLevel/hooks';
import { useAssetAvailabilityFormDrawer } from 'pages/Availability/AssetLevel/useAssetAvailabilityFormDrawer';
import { AvailabilityChartCard } from 'pages/Availability/common/AvailabilityChart/AvailabilityChartCard';
import { buildAvailabilityChartData } from 'pages/Availability/common/AvailabilityChart/utils/chartDataUtils';
import { CancelAndEndModal } from 'pages/Availability/common/Modals/CancelAndEndModal';
import { ReductionInfoModal } from 'pages/Availability/common/Modals/ReductionInfoModal';
import { ReductionsTableCard } from 'pages/Availability/common/ReductionsTable/ReductionsTableCard';
import {
  endOfDayInTimezone,
  getBaseAvailabilityEntriesFromNewAssetAvailabilityEntry,
  PREVIEW_ENTRY_ID,
  startOfDayInTimezone,
} from 'pages/Availability/common/utils';
import {
  AvailabilityChartData,
  BaseAssetAvailabilityEntry,
  Site,
} from 'pages/Availability/types';
import { useMemo } from 'react';
import { useStore } from 'store';
import { TableActionType } from 'store/availability';

interface AssetLevelAvailabilityProps {
  site: Site;
  drawerWidth: string;
}

export const AssetLevelAvailability: React.FC<AssetLevelAvailabilityProps> = ({
  site,
  drawerWidth,
}) => {
  const {
    availabilityFormOpen,
    setAvailabilityFormOpen,
    persistedAssetAvailabilityFormData,
    availabilityDateRange,
    availabilityUserAction,
    setAvailabilityUserAction,
  } = useStore((state) => ({
    availabilityFormOpen: state.availabilityFormOpen,
    setAvailabilityFormOpen: state.setAvailabilityFormOpen,
    persistedAssetAvailabilityFormData:
      state.persistedAssetAvailabilityFormData,
    availabilityDateRange: state.availabilityDateRange,
    availabilityUserAction: state.availabilityUserAction,
    setAvailabilityUserAction: state.setAvailabilityUserAction,
  }));

  const client = useApolloClient();

  // Fetch asset availabilities for all assets
  const { availabilityEntriesFromApi, refetchAssetAvailabilities } =
    useAssetAvailabilities({
      site,
      viewDateRange: {
        start: startOfDayInTimezone(availabilityDateRange.start, UK_TIMEZONE),
        end: endOfDayInTimezone(availabilityDateRange.end, UK_TIMEZONE),
      },
    });

  // Use the form drawer hook
  const { FormDrawer, previewEntries } = useAssetAvailabilityFormDrawer({
    site,
    client,
    title:
      availabilityUserAction.actionType === 'edit'
        ? `Edit reduction with ID ${availabilityUserAction.assetAvailabilityEntry?.id}`
        : 'Report a reduction',
    width: drawerWidth,
    editEntry: availabilityUserAction.assetAvailabilityEntry,
    onRefetch: refetchAssetAvailabilities,
  });

  const hasPreviewEntries = previewEntries.length > 0;
  const viewDateRange = {
    start: hasPreviewEntries
      ? availabilityDateRange.start
      : startOfDayInTimezone(availabilityDateRange.start, UK_TIMEZONE),
    end: hasPreviewEntries
      ? availabilityDateRange.end
      : endOfDayInTimezone(availabilityDateRange.end, UK_TIMEZONE),
  };

  const availabilityEntries = useMemo<BaseAssetAvailabilityEntry[]>(() => {
    // Exclude the entry being edited to avoid duplication
    const filteredApi =
      availabilityUserAction.actionType === 'edit'
        ? availabilityEntriesFromApi.filter(
            (entry) =>
              entry.id !== availabilityUserAction.assetAvailabilityEntry!.id,
          )
        : availabilityEntriesFromApi;

    const hasPersistedDraft =
      availabilityFormOpen &&
      persistedAssetAvailabilityFormData &&
      Object.keys(persistedAssetAvailabilityFormData).length > 0;

    if (previewEntries.length > 0) {
      return [...previewEntries, ...filteredApi];
    }

    if (hasPersistedDraft && persistedAssetAvailabilityFormData.start) {
      return [
        ...getBaseAvailabilityEntriesFromNewAssetAvailabilityEntry(
          PREVIEW_ENTRY_ID,
          persistedAssetAvailabilityFormData,
          site.assets,
        ),
        ...filteredApi,
      ];
    }

    return filteredApi;
  }, [
    previewEntries,
    availabilityEntriesFromApi,
    availabilityFormOpen,
    persistedAssetAvailabilityFormData,
    availabilityUserAction.assetAvailabilityEntry,
  ]);

  // Get asset limits for asset-level availability
  const assetLimits = useMemo<AssetLimitForNetAvailability[]>(() => {
    if (!site.assets) {
      return [];
    }
    return site.assets.flatMap((asset) =>
      asset.assetLimits.map((limit) => ({
        ...limit,
        assetName: asset.name,
      })),
    );
  }, [site.assets]);

  const chartData = useMemo<AvailabilityChartData | null>(
    () =>
      buildAvailabilityChartData({
        availabilityControl: SiteAvailabilityControl.AssetLevel,
        availabilityEntries,
        availabilityFormOpen,
        viewDateRange,
        limits: assetLimits,
        assetNames: (site.assets ?? []).map((it) => it.name),
        previewEntries,
      }),
    [
      availabilityEntries,
      availabilityFormOpen,
      viewDateRange,
      site,
      assetLimits,
      previewEntries,
    ],
  );

  const handleEmptyAvailabilityUserAction = () => {
    setAvailabilityUserAction({
      assetAvailabilityEntry: null,
      siteAvailabilityEntry: null,
      actionType: null,
    });
  };

  return (
    <>
      <Flexbox
        flexDirection={'column'}
        alignItems={'center'}
        style={
          availabilityFormOpen
            ? { width: `calc(100% - ${drawerWidth})` }
            : { width: '100%' }
        }
      >
        <AvailabilityChartCard
          data={chartData}
          overlayMessage={
            availabilityFormOpen && previewEntries.length === 0
              ? 'Select a start and end time to generate this preview.'
              : null
          }
          timezone={UK_TIMEZONE}
        />
        <br />
        <Text prominence="subtle" size={Sizes.Small}>
          All times are shown in UK timezone
        </Text>
        <br />
        <ReductionsTableCard
          availabilityEntries={availabilityEntries}
          availabilityControl={SiteAvailabilityControl.AssetLevel}
          previewMode={availabilityFormOpen}
          timezone={UK_TIMEZONE}
        />
      </Flexbox>

      {FormDrawer}

      {availabilityUserAction.actionType === 'info' &&
        availabilityUserAction.assetAvailabilityEntry && (
          <ReductionInfoModal
            isOpen={availabilityUserAction.actionType === 'info'}
            setIsOpen={() => handleEmptyAvailabilityUserAction()}
            availabilityId={availabilityUserAction.assetAvailabilityEntry.id}
            availabilityControl={SiteAvailabilityControl.AssetLevel}
            timezone={UK_TIMEZONE}
          />
        )}

      {availabilityUserAction.assetAvailabilityEntry &&
        (availabilityUserAction.actionType === 'cancel' ||
          availabilityUserAction.actionType === 'end' ||
          availabilityUserAction.actionType === 'endAndStartNew') && (
          <CancelAndEndModal
            type={availabilityUserAction.actionType}
            onClose={(currentAction: TableActionType) => {
              handleEmptyAvailabilityUserAction();

              if (currentAction === 'endAndStartNew') {
                setAvailabilityFormOpen(true);
              }
              refetchAssetAvailabilities();
            }}
            availabilityId={availabilityUserAction.assetAvailabilityEntry.id}
            availabilityControl={SiteAvailabilityControl.AssetLevel}
          />
        )}
    </>
  );
};
